import { School, Sunrise, Sunset, Tent, HeartHandshake, BookOpen, Baby, Users, HandHeart, Bus, Utensils, Home, Soup } from "lucide-react";
import { HouseHeartIcon } from "@/components/icons/HouseHeartIcon";
import { PeopleChainIcon } from "@/components/icons/PeopleChainIcon";
import { SfaIcon } from "@/components/icons/SfaIcon";

// Registro dei tipi di entità (entity_kind del catalogo servizi lato backend).
// Determina il selettore che il Lavoratore/Ufficio deve usare prima di aprire una
// prestazione. NON usare condizioni rigide del tipo `slug !== 'assistenza_scolastica'`:
// il comportamento è guidato da questa configurazione per singolo servizio.
export const ENTITY_KINDS = {
  school_user: {
    backendKind: "school_user",
    listApi: (key) => `/school-users?service=${key}`,
    adminApi: () => "/admin/school-users",
    searchable: true,
    hint: "Cerca l'utente",
    searchPlaceholder: "Nome o cognome…",
    title: (d) => (d.cognome ? `${d.nome} ${d.cognome}` : d.nome),
    subtitle: (d) => [d.scuola, d.comune, d.plesso].filter(Boolean).join(" · "),
    emptyMessage: "Nessun utente disponibile per questo servizio.",
    showAppaltoBadge: false,
  },
  school_user_ce: {
    backendKind: "school_user",
    listApi: (key) => `/school-users?service=${key}`,
    adminApi: () => "/admin/school-users",
    searchable: true,
    hint: "Cerca l'Utente o il Gruppo",
    searchPlaceholder: "Nome, cognome o nome gruppo…",
    title: (d) => `${(d.ce_config && d.ce_config.tipologia === "gruppo") ? "Gruppo" : "Utente"} · ${d.cognome ? `${d.nome} ${d.cognome}` : d.nome}`,
    subtitle: (d) => [d.comune].filter(Boolean).join(" · ") || "Centro estivo",
    emptyMessage: "Nessun Utente o Gruppo disponibile per il Centro Estivo.",
    showAppaltoBadge: true,
  },
  operative_site: {
    backendKind: "operative_site",
    listApi: (key) => `/operative-sites?service=${key}`,
    adminApi: () => "/admin/operative-sites",
    searchable: false,
    hint: "Seleziona la sede operativa",
    searchPlaceholder: "Filtra le sedi…",
    title: (d) => d.denominazione,
    subtitle: (d) => [d.comune, d.indirizzo].filter(Boolean).join(" · ") || "—",
    emptyMessage: "Nessuna sede operativa disponibile per questo servizio.",
    showAppaltoBadge: true,
  },
  appalto: {
    backendKind: "appalto",
    listApi: (key) => `/service-appalti?service=${key}`,
    adminApi: () => "/my-appalti",
    searchable: false,
    hint: "Apri il servizio",
    searchPlaceholder: "Filtra gli appalti…",
    title: (d) => d.nome,
    subtitle: () => "",
    emptyMessage: "Servizio non disponibile in nessuno dei tuoi appalti.",
    showAppaltoBadge: false,
    autoSelectSingle: true,
  },
};

export function entityKindOf(cfg) {
  return ENTITY_KINDS[cfg?.entityKind] || ENTITY_KINDS.operative_site;
}

// Casa di riposo: il ruolo ASA / OSS usa la Sede operativa (se configurata dall'Ufficio),
// gli altri ruoli restano sull'appalto. La sede è opzionale: se non ne esistono, si ricade sull'appalto.
export const CASA_RIPOSO_KEY = "casa_di_riposo";
export const CASA_RIPOSO_ROLE_ASA = "ASA / OSS";
export function isCasaAsa(svcKey, role) {
  return svcKey === CASA_RIPOSO_KEY && role === CASA_RIPOSO_ROLE_ASA;
}
export function effEntityKindKey(cfg, role, svcKey) {
  if (!cfg) return null;
  if (isCasaAsa(svcKey, role)) return "operative_site";
  return (cfg.roleEntity && role) ? cfg.roleEntity[role] : cfg.entityKind;
}

export const SERVICES = {
  "assistenza-scolastica": {
    key: "assistenza_scolastica",
    label: "Assistenza scolastica",
    short: "Assistenza",
    icon: School,
    entityKind: "school_user",
    roles: null,
    accent: "bg-[#E0F2FE]",
  },
  "pre-scuola": {
    key: "pre_scuola",
    label: "Pre-scuola",
    short: "Pre-scuola",
    icon: Sunrise,
    entityKind: "operative_site",
    roles: ["Educatore", "Sorvegliante"],
    accent: "bg-[#F1F5F9]",
  },
  "post-scuola": {
    key: "post_scuola",
    label: "Post-scuola",
    short: "Post-scuola",
    icon: Sunset,
    entityKind: "operative_site",
    roles: ["Educatore", "Sorvegliante"],
    accent: "bg-[#E2E8F0]",
  },
  "centri-estivi": {
    key: "centro_estivo",
    label: "Centri estivi / Campus",
    short: "Centri estivi",
    icon: Tent,
    entityKind: "operative_site",
    roles: ["Educatore", "Sorvegliante", "Ausiliario", "Referente"],
    // L'entità dipende dal ruolo: Educatore -> Utente/Gruppo (school_user); gli altri -> Sede operativa.
    roleEntity: { Educatore: "school_user_ce", Sorvegliante: "operative_site", Ausiliario: "operative_site", Referente: "operative_site" },
    accent: "bg-[#DBEAFE]",
  },
  "spazio-compiti": {
    key: "spazio_compiti",
    label: "Spazio Compiti",
    short: "Spazio Compiti",
    icon: BookOpen,
    entityKind: "operative_site",
    roles: ["Educatore", "Sorvegliante"],
    accent: "bg-[#F1F5F9]",
  },
  "assistenza-scuolabus": {
    key: "assistenza_scuolabus",
    label: "Assistenza Scuolabus",
    short: "Scuolabus",
    icon: Bus,
    entityKind: "operative_site",
    roles: null,
    accent: "bg-[#FEF3C7]",
  },
  "assistenza-mensa": {
    key: "assistenza_mensa",
    label: "Assistenza Mensa",
    short: "Mensa",
    icon: Utensils,
    entityKind: "operative_site",
    roles: null,
    accent: "bg-[#FEE2E2]",
  },
  "centro-aggregativo": {
    key: "centro_aggregativo",
    label: "Centro aggregativo",
    short: "Centro aggregativo",
    icon: PeopleChainIcon,
    entityKind: "operative_site",
    roles: ["Educatore", "Ausiliario"],
    accent: "bg-[#EDE9FE]",
  },
  "adm": {
    key: "adm",
    label: "ADM",
    short: "ADM",
    icon: HeartHandshake,
    entityKind: "school_user",
    roles: null,
    accent: "bg-[#E0F2FE]",
  },
  "rest": {
    key: "rest",
    label: "ReST",
    short: "ReST",
    // Residenzialità Sociale Temporanea (mostra la descrizione solo negli spazi informativi).
    description: "ReST è un servizio di residenzialità sociale temporanea che supporta famiglie provenienti da altri Paesi nell'inclusione sociale, nella socializzazione e nell'orientamento nella città di Corbetta.",
    icon: Users,
    entityKind: "school_user",
    roles: null,
    accent: "bg-[#E0F2FE]",
  },
  "blue-box": {
    key: "blue_box",
    label: "Blue Box",
    short: "Blue Box",
    icon: HouseHeartIcon,
    entityKind: "appalto",
    roles: null,
    accent: "bg-[#DBEAFE]",
  },
  "casa-di-riposo": {
    key: "casa_di_riposo",
    label: "Casa di riposo",
    short: "Casa di riposo",
    icon: HandHeart,
    entityKind: "appalto",
    roles: ["ASA / OSS", "Infermiere", "Educatore", "Addetto alla cucina"],
    accent: "bg-[#E6F0E8]",
  },
  "nido": {
    key: "nido",
    label: "Nido",
    short: "Nido",
    icon: Baby,
    entityKind: "school_user",
    roles: ["Educatore", "Ausiliario"],
    accent: "bg-[#F1F5F9]",
    // Nel Nido l'entità dipende dal ruolo della prestazione:
    //  - Educatore -> sala (school_user), selezione obbligatoria della sala;
    //  - Ausiliario -> l'appalto (nessuna sala), riusa l'entity_kind "appalto".
    roleEntity: { Educatore: "school_user", Ausiliario: "appalto" },
    // Nel Nido l'entità utente è mostrata come "sala" (solo etichette UI, entità tecnica invariata).
    terms: {
      hint: "Seleziona la sala",
      searchPlaceholder: "Nome della sala…",
      emptyMessage: "Nessuna sala disponibile per questo servizio.",
    },
  },
  "sfa": {
    key: "sfa",
    label: "SFA – Servizio Formazione Autonomie",
    short: "SFA",
    icon: SfaIcon,
    entityKind: "appalto",
    roles: ["Educatore", "Ausiliario"],
    accent: "bg-[#EAF0FB]",
  },
  "vita-quotidiana": {
    key: "vita_quotidiana",
    label: "Vita Quotidiana",
    short: "Vita Quotidiana",
    icon: Home,
    entityKind: "school_user",
    roles: null,
    accent: "bg-[#EAF5EA]",
  },
  "consegna-pasti": {
    key: "consegna_pasti",
    label: "Consegna pasti",
    short: "Consegna pasti",
    icon: Soup,
    entityKind: "appalto",
    roles: null,
    accent: "bg-[#F6F1E8]",
  },
};

export const KEY_TO_SLUG = Object.fromEntries(
  Object.entries(SERVICES).map(([slug, cfg]) => [cfg.key, slug])
);

export function slugFromKey(key) {
  return KEY_TO_SLUG[key] || "assistenza-scolastica";
}

// Etichetta breve da mostrare per un servizio del catalogo (grafica): ADM sempre "ADM".
export function serviceDisplayName(s) {
  if (!s) return "";
  if (s.slug === "adm") return "ADM";
  return s.nome;
}

// Etichette guidate da entity_kind (retrocompatibili con le firme precedenti).
export function entityTitle(entityKind, doc) {
  const k = ENTITY_KINDS[entityKind] || ENTITY_KINDS.operative_site;
  return k.title(doc);
}

export function entitySubtitle(entityKind, doc) {
  const k = ENTITY_KINDS[entityKind] || ENTITY_KINDS.operative_site;
  return k.subtitle(doc);
}

export function fmtDuration(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n)) return "—";
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

export function fmtDateIt(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("it-IT", {
    weekday: "short", day: "2-digit", month: "short",
  });
}
